import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { Badge, Loading, Box, Heading, Text, Textarea, Button, Alert, Stack, FormControl, Input, Progress } from '@/components/lib'
import useTitle from '../hooks/useTitle'
import api from '../api'
import useStore from '../store'
import utils, { buildComponentVisibilityMap, componentConditionIsMet, buildQuestionPages, questionPageIndex, fieldHasValue } from '../utils/utils'

import Terms from './includes/Terms'
import RichTextViewer from './includes/RichTextViewer'
import PhoneInput from './includes/PhoneInput'
import VerificationBox from './includes/VerificationBox'
import useContactVerification from '../hooks/useContactVerification'

import ShortText from './fields/ShortText'
import LongText from './fields/LongText'
import Integer from './fields/Integer'
import Float from './fields/Float'
import Slider from './fields/Slider'
import Rating from './fields/Rating'
import Likert from './fields/Likert'
import TableField from './fields/Table'
import Choice from './fields/Choice'
import DateInput from './fields/Date'
import DatetimeInput from './fields/Datetime'
import TimeInput from './fields/Time'
import Temperature from './fields/Temperature'
import Height from './fields/Height'
import Weight from './fields/Weight'
import BMI from './fields/BMI'
import PromisForm from './fields/PromisForm'
import PromisBattery from './fields/PromisBattery'
import TeethChart from './fields/TeethChart'

export const POLL_FIELDS = {
  text: ShortText,
  paragraph: LongText,
  integer: Integer,
  float: Float,
  slider: Slider,
  rating: Rating,
  likert: Likert,
  choice: Choice,
  date: DateInput,
  datetime: DatetimeInput,
  time: TimeInput,
  temperature: Temperature,
  height: Height,
  weight: Weight,
  bmi: BMI,
  promisForm: PromisForm,
  promisBattery: PromisBattery,
  teethChart: TeethChart,
  table: TableField,
  calculation: null
}

const SUBMIT_PAGE = '__submit__'

export default function Poll () {
  const [trial, setTrial] = useState(null)
  const [stage, setStage] = useState(null)
  const [responses, setResponses] = useState({})
  const [postSubmissionText, setPostSubmissionText] = useState('')
  const [onScreenMessages, setOnScreenMessages] = useState([])
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  // The token the API hands out on the first code request, tying this browser
  // to its verification records, plus the contact value each method was
  // verified for — editing the value has to invalidate the verification.
  const [verificationToken, setVerificationToken] = useState('')
  const [verifiedContacts, setVerifiedContacts] = useState({})
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [errorComponent, setErrorComponent] = useState('')
  // One-question-per-screen mode: the id of a component on the page being
  // shown, or SUBMIT_PAGE for the final screen. Tracked by id rather than by
  // position so a conditional question appearing or disappearing earlier in
  // the stage doesn't move the participant off the question they're on.
  const [currentKey, setCurrentKey] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { stageId, linkId } = useParams()
  const [searchParams] = useSearchParams()
  const linkToken = searchParams.get('token') || ''
  // Set when someone arrives from a consent form's "continue" button. It says
  // which consent to link this response to, and lets the form fill in the
  // contact details they gave a moment ago — it does not stand in for
  // verifying them.
  const consentToken = searchParams.get('consent') || ''
  const [fromConsent, setFromConsent] = useState(false)
  const { setTeam } = useStore()
  useTitle(stage ? stage.name : 'Poll')

  useEffect(() => {
    if (!stageId) setError('No poll could be found.')
    else {
      setLoading(true)
      api.stages.getPoll(stageId, data => {
        setStage(data)
        if (data?.consentHandoff) {
          setFromConsent(true)
          const prefill = data.consentHandoff.prefill || {}
          if (prefill.email) setEmail(prefill.email)
          if (prefill.phone) setPhone(prefill.phone)
        }
        const team = data.ownerTeam
        if (data?.ownerTrial?.sponsor) team.name = data.ownerTrial.sponsor
        if (data?.ownerTrial?.sponsorLogoUrl) team.logoUrl = data.ownerTrial.sponsorLogoUrl
        setTrial(data.ownerTrial)
        setTeam(team)
        setLoading(false)
      }, err => {
        setError(`There was an error fetching the poll: ${err.message}`)
        setLoading(false)
      }, linkToken || undefined, consentToken || undefined)
    }
  }, [stageId, linkToken, consentToken])

  // Contact details this questionnaire asks respondents to verify. Verification
  // is an option on a detail the stage already collects, so both flags matter —
  // and a detail that has to match a consent is always verified too, so that
  // nobody can submit off the back of someone else's consent.
  const verificationMethods = useMemo(() => utils.pollVerificationMethods(stage), [stage])

  // Details that must appear on a consent already completed for this trial.
  const consentedMethods = useMemo(() => {
    const settings = stage?.pollSettings || {}
    const methods = []
    if (settings.requireEmail && settings.requireConsentedEmail) methods.push('email')
    if (settings.requirePhone && settings.requireConsentedPhone) methods.push('phone')
    return methods
  }, [stage])

  const contactFor = method => (method === 'email' ? email : phone)
  const methodLabel = method => (method === 'email' ? 'email address' : 'phone number')
  const isVerified = method =>
    !!contactFor(method) && verifiedContacts[method] === contactFor(method)

  const {
    stateFor: verificationStateFor,
    request: requestVerificationCode,
    verify: verifyCode,
    setCode: setVerificationCode,
    reset: resetVerification
  } = useContactVerification({
    requestCode: (method, done, fail) => {
      api.stages.requestPollVerification(
        stage._id,
        method,
        { contact: contactFor(method), verificationToken: verificationToken || undefined },
        data => {
          setVerificationToken(data.token)
          done(data)
        },
        fail
      )
    },
    submitCode: (method, code, done, fail) => {
      api.stages.verifyPollVerification(
        stage._id,
        method,
        { code, verificationToken },
        done,
        fail
      )
    },
    onVerified: method =>
      setVerifiedContacts(prev => ({ ...prev, [method]: contactFor(method) }))
  })

  // A code was sent to the old value, so changing a contact detail undoes any
  // verification of it and puts the respondent back to "send a code".
  function changeContact (method, value) {
    if (method === 'email') setEmail(value)
    else setPhone(value)
    if (!verificationMethods.includes(method)) return
    if (verifiedContacts[method] === undefined && verificationStateFor(method).status === 'idle') return
    setVerifiedContacts(prev => {
      if (prev[method] === undefined) return prev
      const next = { ...prev }
      delete next[method]
      return next
    })
    resetVerification(method)
  }

  function submit () {
    if (!termsAgreed) return setError('You need to agree to the terms and conditions in order to submit your data.')
    const unverified = verificationMethods.find(method => !isVerified(method))
    if (unverified) {
      return setError(`Please verify your ${methodLabel(unverified)} before submitting your response.`)
    }
    setError()
    setErrorComponent()
    const responseData = {
      termsAgreed,
      response: responses,
      accessId: linkId
    }
    if (linkToken) {
      responseData.token = linkToken
    }
    if (stage.pollSettings?.requireEmail) {
      responseData.email = email
    }
    if (stage.pollSettings?.requirePhone) {
      responseData.phone = phone
    }
    if (verificationMethods.length) {
      responseData.verificationToken = verificationToken
    }
    if (consentToken) {
      responseData.consentToken = consentToken
    }
    setSubmitting(true)
    api.stages.submitPollData(stage._id, responseData, data => {
      // Clear any cached PROMIS form/battery progress so a fresh page load
      // after submitting starts a new questionnaire rather than showing the
      // previous respondent's completed state.
      try {
        Object.keys(window.sessionStorage)
          .filter(k => k.startsWith('promis_session_') || k.startsWith('promis_battery_'))
          .forEach(k => window.sessionStorage.removeItem(k))
      } catch { /* sessionStorage unavailable */ }
      setSuccess(true)
      setSubmitting(false)
      setPostSubmissionText(data.postSubmissionText)
      setOnScreenMessages(data.onScreenMessages || [])
    }, err => {
      let message = err.message || ''
      if (message?.indexOf('[') > -1) {
        const match = err.message.match(/\[([a-z0-9-]+)\]/i)
        setErrorComponent(match[1])
        message = message.replace(match[0], '')
        // In one-per-screen mode the highlighted field is on an earlier page
        if (stage.oneQuestionPerScreen) setCurrentKey(match[1])
      }
      setError(`There was an error submitting your response: ${message}`)
      setSubmitting(false)
    })
  }

  // Build component visibility map to handle chained conditionals
  // This processes components in order and accounts for dependencies (C depends on B depends on A)
  const componentVisibilityMap = useMemo(() => {
    return buildComponentVisibilityMap(stage?.components || [], responses)
  }, [stage?.components, responses])

  function conditionIsMet (c) {
    return componentConditionIsMet(c, componentVisibilityMap)
  }

  // --- One question per screen ---------------------------------------------
  const paged = !!stage?.oneQuestionPerScreen
  const pages = useMemo(() => {
    return paged ? buildQuestionPages(stage?.components || [], componentVisibilityMap) : []
  }, [paged, stage?.components, componentVisibilityMap])
  // The submit page follows the last question; a stage with nothing visible
  // to ask goes straight to it.
  const onSubmitPage = paged && (currentKey === SUBMIT_PAGE || pages.length === 0)
  const pageIndex = onSubmitPage ? pages.length : Math.max(questionPageIndex(pages, currentKey), 0)
  const page = pages[pageIndex] || []
  const pageQuestion = page.find(c => c.dataType)
  // Next is held back on an unanswered required question, by the same rule
  // the API applies on submit, so the participant isn't bounced back several
  // screens at the end. Partial submission switches that rule off there too.
  const nextBlocked = !!pageQuestion?.isRequired && !stage?.allowIncompleteSubmission &&
    !fieldHasValue(pageQuestion.type, responses[pageQuestion.dataType]?.value)

  function goToPage (index) {
    if (index >= pages.length) setCurrentKey(SUBMIT_PAGE)
    else setCurrentKey((pages[index].find(c => c.dataType) || pages[index][0]).id)
    setError()
  }

  useEffect(() => {
    if (paged) window.scrollTo({ top: 0 })
  }, [paged, pageIndex])

  function renderComponent (component) {
    if (!conditionIsMet(component)) return null
    if (component.type === 'title') {
      return (
        <Box key={component.id} p={2} bg='bg.accent'>
          <Heading key={component.id} size='md'>{component.title}</Heading>
        </Box>
      )
    }
    if (component.type === 'sectionDivider') {
      return (
        <Box key={component.id} p={2} bg='bg.accent'>
          <Heading size='md'>{component.title}</Heading>
          <RichTextViewer content={component.description} />
        </Box>
      )
    }
    if (component.type?.type === 'calculation') return null
    const Field = POLL_FIELDS[component.type?.type]
    const value = responses[component.dataType]?.value
    const onChange = v => setResponses({
      ...responses,
      [component.dataType]: {
        value: v,
        createdAt: new Date()
      }
    })
    return (
      <Stack gap={3} key={component.id} p={3} borderWidth='1px' rounded='md' bg={errorComponent === component.id ? 'red.100' : 'bg.surface'}>
        {component.isRequired &&
          <Box display='flex' justifyContent='flex-end'>
            <Badge colorPalette='red'>Required</Badge>
          </Box>}

        <Heading as='h3' size='md'>{component.title}</Heading>
        <RichTextViewer content={component.description} />
        <Box>
          {Field
            ? <Field dataType={component.type} stageId={stageId} value={value} onChange={onChange} />
            : <Alert variant='left-accent'>This question type is not yet supported in polls.</Alert>}
        </Box>
      </Stack>
    )
  }

  // 720px, not the 500px the other public pages use: a Likert renders as a grid
  // from the `md` breakpoint up, and its columns need roughly 9rem plus one per
  // scale point to sit side by side. At 500px that grid was wider than the page
  // and scrolled sideways. maxW is only a ceiling, so narrower viewports still
  // fill the screen, and below `md` the Likert stacks instead.
  return (
    <Stack gap={5} maxW='720px' mx='auto' p={5}>

      <Heading size='lg'>Welcome</Heading>
      {linkToken && stage?.participantId && (
        <Box p={3} borderWidth='1px' borderColor='border.default' bg='bg.canvas' rounded='md'>
          <Text fontSize='sm' color='fg.muted'>
            This form will submit data as participant{' '}
            <Text as='span' fontWeight='semibold' color='fg'>
              {stage.participantId}
            </Text>
            .
          </Text>
        </Box>
      )}
      {loading &&
        <Box display='flex' justifyContent='center'>
          <Loading />
        </Box>}

      {stage &&
        <>
          <Heading size='md'>{stage.name}</Heading>
          <Text color='fg.muted'>{stage.description}</Text>

          {fromConsent && !success &&
            <Alert status='success' variant='left-accent'>
              Your consent has been recorded. This is the next step, and your
              answers here will be linked to the consent you have just given.
            </Alert>}

          {success
            ? (
              <>
                <Alert status='success' title='Thank you'>
                  Your response has been submitted successfully.
                </Alert>
                {postSubmissionText && <RichTextViewer content={postSubmissionText} />}
                {onScreenMessages.map((message, i) => (
                  <RichTextViewer key={i} content={message} />
                ))}
              </>)
            : (
              <>
                {stage.showProgressBar && paged &&
                  <Stack gap={1}>
                    <Text fontSize='sm' color='fg.muted'>
                      {onSubmitPage ? 'Ready to submit' : `Question ${pageIndex + 1} of ${pages.length}`}
                    </Text>
                    <Progress w='100%' value={(pageIndex / (pages.length + 1)) * 100} />
                  </Stack>}

                {paged && !onSubmitPage &&
                  <Stack gap={5}>
                    {page.map(renderComponent)}
                    {error && <Alert status='error'>{error}</Alert>}
                    {nextBlocked &&
                      <Text fontSize='sm' color='fg.muted'>Please answer this question to continue.</Text>}
                    <Box display='flex' justifyContent='space-between' gap={3}>
                      <Button variant='outline' onClick={() => goToPage(pageIndex - 1)} visibility={pageIndex > 0 ? 'visible' : 'hidden'}>Back</Button>
                      <Button colorPalette='primary' onClick={() => goToPage(pageIndex + 1)} disabled={nextBlocked}>Next</Button>
                    </Box>
                  </Stack>}
                {!paged &&
                  <Stack spacing={5}>
                    {stage.components?.map(renderComponent)}
                  </Stack>}

                {(!paged || onSubmitPage) &&
                  <>
                    {(stage.pollSettings?.requireEmail || stage.pollSettings?.requirePhone) &&
                      <Stack gap={3} p={3} bg='bg.accentSubtle' rounded='md'>
                        <Box>
                          <Heading size='md'>Contact details</Heading>
                          <Text>
                            In order to submit your response, we need to collect your contact details.
                            {verificationMethods.length > 0 && ' We\u2019ll send you a code to confirm them.'}
                            {consentedMethods.length > 0 &&
                              ` Please give the ${consentedMethods.map(methodLabel).join(' and ')} you used when you consented to this trial \u2014 we can only accept your response if it matches.`}
                          </Text>
                        </Box>

                        {stage.pollSettings?.requireEmail &&
                          <FormControl label='Email address' isRequired>
                            {isVerified('email')
                              ? (
                                <Stack gap={2} alignItems='flex-start'>
                                  <Alert status='success' variant='left-accent'>Email address verified: {email}</Alert>
                                  <Button size='xs' variant='ghost' onClick={() => changeContact('email', '')}>
                                    Use a different email address
                                  </Button>
                                </Stack>
                                )
                              : (
                                <>
                                  <Input bg='bg.surface' type='email' value={email} onChange={e => changeContact('email', e.target.value)} placeholder='jane@exmaple.com' />
                                  {verificationMethods.includes('email') &&
                                    <VerificationBox
                                      vs={verificationStateFor('email')}
                                      contactValue={email}
                                      onRequestCode={() => requestVerificationCode('email')}
                                      onCodeChange={val => setVerificationCode('email', val)}
                                      onVerify={() => verifyCode('email')}
                                    />}
                                </>
                                )}
                          </FormControl>}

                        {stage.pollSettings?.requirePhone &&
                          <FormControl label='Phone number' isRequired>
                            {isVerified('phone')
                              ? (
                                <Stack gap={2} alignItems='flex-start'>
                                  <Alert status='success' variant='left-accent'>Phone number verified: {phone}</Alert>
                                  <Button size='xs' variant='ghost' onClick={() => changeContact('phone', '')}>
                                    Use a different phone number
                                  </Button>
                                </Stack>
                                )
                              : (
                                <>
                                  <PhoneInput
                                    bg='bg.surface'
                                    value={phone}
                                    onChange={v => changeContact('phone', v)}
                                    placeholder='Enter your phone number'
                                  />
                                  {verificationMethods.includes('phone') &&
                                    <VerificationBox
                                      vs={verificationStateFor('phone')}
                                      contactValue={phone}
                                      onRequestCode={() => requestVerificationCode('phone')}
                                      onCodeChange={val => setVerificationCode('phone', val)}
                                      onVerify={() => verifyCode('phone')}
                                    />}
                                </>
                                )}
                          </FormControl>}
                      </Stack>}

                    <Stack gap={3} p={3} bg='bg.subtle' rounded='md'>
                      <Heading size='md'>Conditions & privacy</Heading>

                      {trial?.conditions && (
                        <>
                          <Textarea readOnly bg='bg.surface' rows={8} value={trial.conditions} />
                        </>
                      )}

                      <Terms value={termsAgreed} onChange={setTermsAgreed} agreementStatement='I have read and I agree to the trial conditions. I have also read and I agree to the Trialflare conditions and privacy statement' />
                    </Stack>
                    {error && <Alert status='error'>{error}</Alert>}
                    {paged && pages.length > 0 &&
                      <Button variant='outline' onClick={() => goToPage(pages.length - 1)}>Back</Button>}
                    <Button w='100%' colorPalette='primary' onClick={submit} isLoading={submitting}>Submit</Button>
                  </>}
              </>)}
        </>}

    </Stack>
  )
};
